import { CasinoPage, CasinoPageSeoContext, CasinoPageSeoEditor, CasinoPageTab, CasinoTabSeo, SaveCasinoTabSeoRequest, SeoMetadata } from "../../http/protocol";
import { SeoField, SeoMetadataByLocaleType, SeoMetadataByTabType } from "../seo/seoTypes";
import { entriesInServedLocales, hasInvalidOgImageUrl, SeoDraftStore, seoByLocale, SeoSaveError, SeoSaveFailureCodeType, withSeoField } from "./seoDraftStore";

/** The save failures of a casino page draft, which adds the ways its tabs can fail to match the saved page. */
export type CasinoPageSeoSaveFailureCodeType = SeoSaveFailureCodeType | "tab-count-mismatch" | "page-without-id" | "tab-missing" | "tab-order-mismatch";

const pageSeoSaveError = (code: CasinoPageSeoSaveFailureCodeType): SeoSaveError<CasinoPageSeoSaveFailureCodeType> =>
  new SeoSaveError<CasinoPageSeoSaveFailureCodeType>(code);

/** The part of the casino page service the draft loads and saves through. */
export interface CasinoPageSeoServiceType {
  getSeo(casinoPageId: number): Promise<CasinoPageSeoEditor>;
  saveSeo(casinoPageId: number, request: SaveCasinoTabSeoRequest): Promise<CasinoTabSeo>;
}

/*
 * A symbol key survives the object spreads the tab editors copy tabs with, and JSON.stringify skips
 * it, so a new tab keeps its client key while the page request stays exactly what the backend expects.
 */
const CLIENT_KEY = Symbol("casinoPageTabClientKey");

type CasinoPageTabWithClientKeyType = CasinoPageTab & { [CLIENT_KEY]?: string };

let nextClientKey = 1;

/** A copy of a not-yet-saved tab carrying a client key its SEO draft is filed under until the tab has an id. */
export function withSeoClientKey(tab: CasinoPageTab): CasinoPageTab {
  const copy: CasinoPageTabWithClientKeyType = { ...tab };
  copy[CLIENT_KEY] = `new-${nextClientKey}`;
  nextClientKey += 1;
  return copy;
}

/** The key a tab's SEO draft is filed under: its id once saved, its client key before that. */
export function seoTabKey(tab: CasinoPageTab): string | undefined {
  if (tab.id !== undefined && tab.id !== null) {
    return String(tab.id);
  }
  return (tab as CasinoPageTabWithClientKeyType)[CLIENT_KEY];
}

/**
 * The SEO values being edited for each tab of a casino page. Every tab editor of the page shares one
 * draft, keyed by the page id. Values are only sent after the page itself is saved, since new tabs
 * have no id to store SEO under before.
 */
export class CasinoPageSeoDraft extends SeoDraftStore<CasinoPageSeoEditor> {
  private context?: CasinoPageSeoContext;
  private tabs: SeoMetadataByTabType = {};

  constructor(private readonly service: CasinoPageSeoServiceType) {
    super();
  }

  protected fetch(casinoPageId: number): Promise<CasinoPageSeoEditor> {
    return this.service.getSeo(casinoPageId);
  }

  protected applyLoaded(editor: CasinoPageSeoEditor): void {
    this.context = editor.context;
    this.replaceValues(editor.seo);
  }

  getContext(): CasinoPageSeoContext | undefined {
    return this.context;
  }

  getTabValues(tabKey: string): SeoMetadataByLocaleType {
    return this.tabs[tabKey] ?? {};
  }

  setTabField(tabKey: string, locale: string, field: SeoField, value: string): void {
    this.tabs = { ...this.tabs, [tabKey]: withSeoField(this.getTabValues(tabKey), locale, field, value) };
    this.notify();
  }

  /**
   * Saves the drafts against the page the backend just stored. The saved page lists its tabs in
   * the order they were submitted, so a new tab takes the id at its position; drafts of tabs no
   * longer on the page are dropped. The drafts are re-filed under the new ids before the SEO
   * request goes out, so a failed request can be retried with the next page save.
   */
  async saveAfterPageSave(savedPage: CasinoPage, submittedTabs: CasinoPageTab[]): Promise<void> {
    const savedTabs = savedPage.tabs ?? [];
    if (savedTabs.length !== submittedTabs.length) {
      throw pageSeoSaveError("tab-count-mismatch");
    }
    if (savedPage.id === undefined) {
      throw pageSeoSaveError("page-without-id");
    }
    this.tabs = this.refiledTabs(savedTabs, submittedTabs);
    this.notify();

    const request = this.buildRequest();
    if (request === undefined) {
      return;
    }
    if (hasInvalidOgImageUrl(Object.values(request.tabs ?? {}).flat())) {
      throw pageSeoSaveError("invalid-og-image-url");
    }
    const pageId = savedPage.id;
    const saved = await this.whileSaving(() => this.service.saveSeo(pageId, request));
    this.replaceValues(saved);
  }

  private refiledTabs(savedTabs: CasinoPageTab[], submittedTabs: CasinoPageTab[]): SeoMetadataByTabType {
    const savedIds = new Set(savedTabs.map((tab) => tab.id));
    const existingIds = new Set(submittedTabs.map((tab) => tab.id).filter((id) => id !== undefined && id !== null));
    const assignedIds = new Set<number>();
    const refiled: SeoMetadataByTabType = {};
    submittedTabs.forEach((submitted, position) => {
      const key = seoTabKey(submitted);
      const isNew = submitted.id === undefined || submitted.id === null;
      const savedId = isNew ? savedTabs[position].id : submitted.id;
      if (savedId === undefined || savedId === null || !savedIds.has(savedId)) {
        throw pageSeoSaveError("tab-missing");
      }
      // A new tab landing on an existing tab's id means the saved order differs from the submitted one,
      // and its draft would overwrite that tab's.
      if (assignedIds.has(savedId) || (isNew && existingIds.has(savedId))) {
        throw pageSeoSaveError("tab-order-mismatch");
      }
      assignedIds.add(savedId);
      const draft = key === undefined ? undefined : this.tabs[key];
      if (draft !== undefined) {
        refiled[String(savedId)] = draft;
      }
    });
    return refiled;
  }

  /** Undefined when there is nothing to send, e.g. a page whose SEO was never loaded. */
  private buildRequest(): SaveCasinoTabSeoRequest | undefined {
    const tabs: { [tabId: string]: SeoMetadata[] } = {};
    Object.entries(this.tabs).forEach(([tabId, values]) => {
      const tabEntries = entriesInServedLocales(values, this.context?.locales);
      if (tabEntries.length > 0) {
        tabs[tabId] = tabEntries;
      }
    });
    if (Object.keys(tabs).length === 0) {
      return undefined;
    }
    return { tabs };
  }

  private replaceValues(seo?: CasinoTabSeo): void {
    const tabs: SeoMetadataByTabType = {};
    Object.entries(seo?.tabs ?? {}).forEach(([tabId, entries]) => {
      tabs[tabId] = seoByLocale(entries);
    });
    this.tabs = tabs;
    this.notify();
  }
}
